import { apiFetch, createApiUrl } from "./api";

/**
 * Upload an AI input asset with upload progress (XHR).
 * Backend expects multipart form-data:
 * - website_id
 * - file OR image
 * - comment (optional)
 */
export function uploadAIInputAsset({
  websiteId,
  file,
  assetType, // 'file' | 'image'
  comment,
  onProgress,
  signal,
}) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("file is required"));
    const type = assetType === "image" ? "image" : "file";

    const formData = new FormData();
    if (websiteId) formData.append("website_id", String(websiteId));
    formData.append(type, file);
    if (comment != null && String(comment).trim() !== "") {
      formData.append("comment", String(comment));
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", createApiUrl("/api/ai/input-assets/upload/"), true);
    xhr.withCredentials = true;
    xhr.timeout = 5 * 60 * 1000;

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const percent = Math.round((evt.loaded / evt.total) * 100);
      onProgress?.(percent);
    };

    xhr.onerror = () => reject(new Error("Network error uploading asset"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));

    xhr.onload = () => {
      try {
        const json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(json);
        } else {
          reject(new Error(json?.error || `Upload failed (${xhr.status})`));
        }
      } catch (e) {
        reject(new Error("Invalid server response"));
      }
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener(
        "abort",
        () => {
          try {
            xhr.abort();
          } catch (e) {
            // no-op
          }
        },
        { once: true }
      );
    }

    xhr.send(formData);
  });
}

export async function listAIInputAssets({ websiteId } = {}) {
  const params = new URLSearchParams();
  if (websiteId) params.set("website_id", String(websiteId));
  const endpoint = `/api/ai/input-assets/${params.toString() ? `?${params.toString()}` : ""}`;
  const resp = await apiFetch(endpoint, { method: "GET" });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to load assets");
  }
  return data;
}

export async function updateAIInputAssetComment(assetId, comment) {
  return updateAIInputAsset(assetId, { comment });
}

export async function updateAIInputAsset(assetId, { comment, extractedTextUser, detachWebsiteId, retryExtraction } = {}) {
  const body = {};
  if (comment !== undefined) body.comment = comment;
  if (extractedTextUser !== undefined) body.extracted_text_user = extractedTextUser;
  if (detachWebsiteId !== undefined) body.detach_website_id = detachWebsiteId;
  if (retryExtraction !== undefined) body.retry_extraction = retryExtraction;

  const resp = await apiFetch(`/api/ai/input-assets/${assetId}/update/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to update asset");
  }
  return data;
}

export async function deleteAIInputAsset(assetId) {
  const resp = await apiFetch(`/api/ai/input-assets/${assetId}/delete/`, {
    method: "DELETE",
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to delete asset");
  }
  return data;
}

export async function detachAIInputAssetFromWebsite(assetId, websiteId) {
  const params = new URLSearchParams();
  params.set("website_id", String(websiteId));
  const resp = await apiFetch(`/api/ai/input-assets/${assetId}/delete/?${params.toString()}`, {
    method: "DELETE",
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to detach asset");
  }
  return data;
}

export async function getAIInputAsset(assetId) {
  const resp = await apiFetch(`/api/ai/input-assets/${assetId}/`, { method: "GET" });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to load asset");
  }
  return data;
}

export async function retryAIInputAssetExtraction(assetId) {
  const resp = await apiFetch(`/api/ai/input-assets/${assetId}/update/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ retry_extraction: true }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.error || "Failed to retry extraction");
  }
  return data;
}

